import React from 'react';
import { X } from 'lucide-react';
import { Device } from '../types/meeting.types';
import {
  VIDEO_QUALITY_OPTIONS,
  getQualityLabel,
  type VideoQualityPreference,
  type VideoQualityTier,
} from '../utils/videoQuality.ts';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableCameras: Device[];
  availableMicrophones: Device[];
  selectedCamera: string;
  selectedMicrophone: string;
  videoQualityPreference: VideoQualityPreference;
  effectiveVideoQuality: VideoQualityTier;
  onCameraChange: (deviceId: string) => void;
  onMicrophoneChange: (deviceId: string) => void;
  onVideoQualityChange: (quality: VideoQualityPreference) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  availableCameras,
  availableMicrophones,
  selectedCamera,
  selectedMicrophone,
  videoQualityPreference,
  effectiveVideoQuality,
  onCameraChange,
  onMicrophoneChange,
  onVideoQualityChange
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl border border-white/10 w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Ayarlar</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition"
            title="Kapat"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Kamera
            </label>
            <select
              value={selectedCamera}
              onChange={(e) => onCameraChange(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-700 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 appearance-none cursor-pointer"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px' }}
              title="Kamera seçin"
            >
              {availableCameras.map((camera) => (
                <option key={camera.deviceId} value={camera.deviceId} className="bg-slate-700 text-white">
                  {camera.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Mikrofon
            </label>
            <select
              value={selectedMicrophone}
              onChange={(e) => onMicrophoneChange(e.target.value)}
              className="w-full px-4 py-2.5 bg-slate-700 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 appearance-none cursor-pointer"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px' }}
              title="Mikrofon seçin"
            >
              {availableMicrophones.map((microphone) => (
                <option key={microphone.deviceId} value={microphone.deviceId} className="bg-slate-700 text-white">
                  {microphone.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Video Kalitesi
            </label>
            <select
              value={videoQualityPreference}
              onChange={(e) => onVideoQualityChange(e.target.value as VideoQualityPreference)}
              className="w-full px-4 py-2.5 bg-slate-700 border border-white/10 rounded-lg text-white focus:outline-none focus:border-purple-500 appearance-none cursor-pointer"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px' }}
              title="Video kalitesi seçin"
            >
              {VIDEO_QUALITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="bg-slate-700 text-white">
                  {option.label} ({option.detail})
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-400">
              {videoQualityPreference === 'auto'
                ? `Şu an: ${getQualityLabel(effectiveVideoQuality)} — ağ durumuna göre otomatik ayarlanıyor.`
                : 'Gerçek kalite kamera, tarayıcı ve ağ koşullarına göre değişebilir.'}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;